// src/services/productCatalogAPI.ts
// Product Catalog API service for qmLEO
// Handles all product catalog CRUD operations
// Uses fetch and authService pattern (consistent with inventoryAPI)

import { authService } from './authService';
import { logger } from '../utils/logger';
import { getApiBaseUrl } from './configService';

const productLogger = logger.createServiceLogger('ProductCatalogAPI');

// Product type definition
export interface Product {
  productId: string;
  tenantId: string;
  name: string;
  manufacturer?: string;
  model?: string;
  categoryId: string;
  subcategoryId?: string;
  productType: 'SERIALIZED' | 'BULK';
  trackingType?: 'SERIALIZED' | 'BULK_TRACKED' | 'CONSUMABLE';
  requiresSerialNumber: boolean;
  typicalUnitCost?: number;
  preferredVendor?: string;
  defaultLocation?: string;
  minStockLevel?: number;
  reorderPoint?: number;
  unitOfMeasure?: string;
  description?: string;
  standardExpirationMonths?: number;  // Default warranty/expiration period in months
  isActive: boolean;
  isDiscontinued: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedByName?: string;
}

// Request types
export interface CreateProductRequest {
  name: string;
  manufacturer?: string;
  model?: string;
  categoryId: string;
  subcategoryId?: string;
  parentCategoryId?: string;
  productType: 'SERIALIZED' | 'BULK';
  trackingType?: 'SERIALIZED' | 'BULK_TRACKED' | 'CONSUMABLE';
  requiresSerialNumber?: boolean;
  typicalUnitCost?: number;
  preferredVendor?: string;
  defaultLocation?: string;
  minStockLevel?: number;
  reorderPoint?: number;
  unitOfMeasure?: string;
  description?: string;
  standardExpirationMonths?: number;  // Default warranty/expiration period in months
}

export interface UpdateProductRequest {
  name?: string;
  manufacturer?: string;
  model?: string;
  categoryId?: string;
  subcategoryId?: string;
  typicalUnitCost?: number;
  preferredVendor?: string;
  defaultLocation?: string;
  minStockLevel?: number;
  reorderPoint?: number;
  unitOfMeasure?: string;
  description?: string;
  standardExpirationMonths?: number;  // Default warranty/expiration period in months
  isActive?: boolean;
  isDiscontinued?: boolean;
}

export interface ProductFilters {
  categoryId?: string;
  active?: boolean;
  search?: string;
  productType?: 'SERIALIZED' | 'BULK';
}

// API response types
interface APIResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
  };
}

// Product Catalog API class
class ProductCatalogAPI {
  private baseUrl: string;

  constructor() {
    this.baseUrl = getApiBaseUrl();
  }

  /**
   * Make authenticated request (matches inventoryAPI pattern)
   */
  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<APIResponse<T>> {
    const endTimer = logger.startTimer('ProductCatalogAPI', `${options.method || 'GET'} ${endpoint}`);
    
    logger.logAPIRequest('ProductCatalogAPI', options.method || 'GET', `${this.baseUrl}${endpoint}`, options.body);

    const hasBody = options.body !== undefined && options.body !== null;
    const headers = new Headers(options.headers || {});
    if (hasBody && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }

    try {
      const response = await authService.makeAuthenticatedRequest(
        `${this.baseUrl}${endpoint}`,
        { ...options, headers }
      );

      logger.logAPIResponse('ProductCatalogAPI', options.method || 'GET', endpoint, response.status);
      endTimer();

      const data = await response.json();

      if (!response.ok) {
        productLogger.error(`Request failed with status ${response.status}`, { 
          endpoint, 
          status: response.status, 
          data 
        });
        return {
          success: false,
          error: data.error || {
            code: 'HTTP_ERROR',
            message: `HTTP ${response.status}`,
          },
        };
      }

      return data as APIResponse<T>;
    } catch (error: any) {
      endTimer();
      productLogger.error('Request failed', { endpoint, method: options.method }, error);

      if (error.code === 'NO_SESSION') {
        return {
          success: false,
          error: { code: 'AUTH_ERROR', message: 'Please log in again' },
        };
      }

      if (error.code === 'TOKEN_EXPIRED') {
        return {
          success: false,
          error: { code: 'AUTH_ERROR', message: 'Session expired, please refresh' },
        };
      }

      return {
        success: false,
        error: { code: 'REQUEST_ERROR', message: error.message || 'Request failed' },
      };
    }
  }

  /**
   * List all products with optional filters
   */
  async listProducts(filters?: ProductFilters): Promise<Product[]> {
    productLogger.debug('Listing products with filters', filters);
    
    const params = new URLSearchParams();
    if (filters?.categoryId) params.append('categoryId', filters.categoryId);
    if (filters?.active !== undefined) params.append('active', filters.active.toString());
    if (filters?.search) params.append('search', filters.search);
    if (filters?.productType) params.append('productType', filters.productType);
    
    const endpoint = `/products${params.toString() ? `?${params}` : ''}`;
    
    const response = await this.makeRequest<Product[]>(endpoint);
    
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to fetch products');
    }
    
    productLogger.info('Products fetched', { count: response.data?.length || 0 });
    return response.data || [];
  }

  /**
   * Get single product by ID
   */
  async getProduct(productId: string): Promise<Product> {
    productLogger.debug('Fetching product', { productId });
    
    const encodedProductId = encodeURIComponent(productId);
    const response = await this.makeRequest<Product>(`/products/${encodedProductId}`);
    
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to fetch product');
    }
    
    productLogger.info('Product fetched', { productId, name: response.data?.name });
    return response.data!;
  }

  /**
   * Create new product
   */
  async createProduct(product: CreateProductRequest): Promise<Product> {
    productLogger.info('Creating product', { name: product.name, type: product.productType });
    
    // Set requiresSerialNumber based on productType if not explicitly set
    const productData = {
      ...product,
      requiresSerialNumber: product.requiresSerialNumber ?? (product.productType === 'SERIALIZED')
    };
    
    const response = await this.makeRequest<Product>('/products', {
      method: 'POST',
      body: JSON.stringify(productData),
    });
    
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to create product');
    }
    
    productLogger.info('Product created', { productId: response.data?.productId });
    return response.data!;
  }

  /**
   * Update existing product
   */
  async updateProduct(productId: string, updates: UpdateProductRequest): Promise<Product> {
    productLogger.info('Updating product', { productId, fieldsUpdated: Object.keys(updates) });
    
    const encodedProductId = encodeURIComponent(productId);
    const response = await this.makeRequest<Product>(`/products/${encodedProductId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to update product');
    }
    
    productLogger.info('Product updated', { productId, name: response.data?.name });
    return response.data!;
  }

  /**
   * Delete product (soft delete - marks as inactive)
   */
  async deleteProduct(productId: string): Promise<void> {
    productLogger.warn('Deleting product', { productId });
    
    const encodedProductId = encodeURIComponent(productId);
    const response = await this.makeRequest<void>(`/products/${encodedProductId}`, {
      method: 'DELETE',
    });
    
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to delete product');
    }
    
    productLogger.info('Product deleted', { productId });
  }

  /**
   * Get inventory summary for a product
   */
  async getProductInventorySummary(productId: string): Promise<{
    totalInstances: number;
    availableInstances: number;
    assignedInstances: number;
    maintenanceInstances: number;
  }> {
    productLogger.debug('Fetching inventory summary for product', { productId });
    
    const encodedProductId = encodeURIComponent(productId);
    const response = await this.makeRequest<any>(`/inventory-instances?productId=${encodedProductId}`);
    
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to fetch inventory summary');
    }
    
    const instances = response.data || [];
    
    const summary = {
      totalInstances: instances.length,
      availableInstances: instances.filter((i: any) => i.status === 'AVAILABLE').length,
      assignedInstances: instances.filter((i: any) => i.status === 'ASSIGNED').length,
      maintenanceInstances: instances.filter((i: any) => i.status === 'MAINTENANCE').length
    };
    
    productLogger.info('Inventory summary fetched', { productId, ...summary });
    return summary;
  }
}

// Export singleton instance
export const productCatalogAPI = new ProductCatalogAPI();
